import React from "react";
import {
	Dropdown,
	Option,
	useId,
	makeStyles,
	tokens,
	Label,
	Spinner,
	Tooltip,
	Button,
	Text,
} from "@fluentui/react-components";
import {
	Copy16Regular,
	Checkmark16Regular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: tokens.spacingVerticalXXS,
		width: "100%",
		marginBottom: tokens.spacingVerticalM,
	},
	errorText: {
		color: tokens.colorPaletteRedForeground1,
		fontSize: tokens.fontSizeBase200,
	},
	labelRow: {
		display: "flex",
		alignItems: "center",
		gap: tokens.spacingHorizontalXXS,
	},
	copyButton: {
		minWidth: "auto",
		padding: "2px",
	},
	highlighted: {
		display: "flex",
		flexDirection: "column",
		gap: tokens.spacingVerticalXXS,
		// width: '100%',
		marginBottom: tokens.spacingVerticalM,
		border: `2px solid ${tokens.colorPaletteGreenBorder2}`,
		borderRadius: tokens.borderRadiusMedium,
		padding: tokens.spacingHorizontalS,
		backgroundColor: tokens.colorPaletteGreenBackground1,
	},
	helperText: {
		color: tokens.colorPaletteGreenForeground1,
		fontSize: tokens.fontSizeBase200,
		lineHeight: tokens.lineHeightBase200,
	},
});

export interface SelectOption {
	key: string;
	text: string;
	disabled?: boolean;
}

interface SelectProps {
	label: string;
	options: SelectOption[];
	value?: string | string[];
	onChange: (value: string | string[]) => void;
	multiselect?: boolean;
	placeholder?: string;
	disabled?: boolean;
	required?: boolean;
	loading?: boolean;
	error?: boolean;
	/** If provided, shows a copy icon next to the label. The tooltip will say "Copy {copyLabel}". */
	copyLabel?: string;
	/** When true, wraps the dropdown in a green highlight with optional helper text. */
	isHighlighted?: boolean;
	/** Helper text shown below the dropdown when isHighlighted is true. */
	helperText?: string;
}

export const Select: React.FC<SelectProps> = ({
	label,
	options,
	value,
	onChange,
	multiselect = false,
	placeholder = "Select an option",
	disabled = false,
	required = false,
	loading = false,
	error = false,
	copyLabel,
	isHighlighted = false,
	helperText,
}) => {
	const dropdownId = useId("dropdown");
	const styles = useStyles();
	const [copied, setCopied] = React.useState(false);

	const handleChange = (_event: any, data: any) => {
		if (multiselect) {
			onChange(data.selectedOptions as string[]);
		} else {
			// For single select, data.optionValue contains the selected value
			onChange(data.optionValue as string);
		}
	};

	const selectedText = React.useMemo(() => {
		if (multiselect) {
			return (Array.isArray(value) ? value : [])
				.map(
					(v) => options.find((opt) => opt.key === v)?.text,
				)
				.filter(Boolean)
				.join(", ");
		}
		return (
			options.find((opt) => opt.key === value)?.text || ""
		);
	}, [value, options, multiselect]);

	const handleCopy = () => {
		if (!selectedText) return;

		const copyWithFallback = async () => {
			try {
				await navigator.clipboard.writeText(selectedText);
			} catch {
				// Fallback for Office Add-in webview where clipboard API is blocked
				const textarea = document.createElement("textarea");
				textarea.value = selectedText;
				textarea.style.position = "fixed";
				textarea.style.opacity = "0";
				document.body.appendChild(textarea);
				textarea.select();
				document.execCommand("copy");
				document.body.removeChild(textarea);
			}
		};

		copyWithFallback();
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	};

	const selectedOptions = React.useMemo(() => {
		if (multiselect) {
			return Array.isArray(value) ? value : [];
		}
		return typeof value === "string" ? [value] : [];
	}, [value, multiselect]);

	return (
		<div
			className={
				isHighlighted ? styles.highlighted : styles.root
			}
		>
			<div className={styles.labelRow}>
				<Label id={dropdownId} required={required}>
					{label}
				</Label>
				{copyLabel && selectedText && (
					<Tooltip
						content={
							copied ? "Copied!" : `Copy ${copyLabel}`
						}
						relationship="label"
					>
						<Button
							className={styles.copyButton}
							appearance="subtle"
							size="small"
							icon={
								copied ? (
									<Checkmark16Regular />
								) : (
									<Copy16Regular />
								)
							}
							onClick={handleCopy}
						/>
					</Tooltip>
				)}
			</div>
			<Dropdown
				aria-labelledby={dropdownId}
				placeholder={
					error
						? "Failed to load"
						: loading
							? "Loading..."
							: placeholder
				}
				multiselect={multiselect}
				value={selectedText}
				selectedOptions={selectedOptions}
				onOptionSelect={handleChange}
				disabled={disabled || loading || error}
			>
				{loading ? (
					<Option key="__loading" value="" text="" disabled>
						<Spinner size="tiny" label="Loading..." />
					</Option>
				) : (
					options.map((option) => (
						<Option
							key={option.key}
							value={option.key}
							text={option.text}
							disabled={option.disabled}
						>
							{option.text}
						</Option>
					))
				)}
			</Dropdown>
			{error && (
				<Text className={styles.errorText}>
					Something went wrong. Please try again later.
				</Text>
			)}
			{isHighlighted && helperText && (
				<Text className={styles.helperText}>
					{helperText}
				</Text>
			)}
		</div>
	);
};

export default Select;
